import React from "react";

import ImagePlaceholder from "../ImagePlaceholder";
import surpriseHero from "../../assets/img/explore/lighthouse.jpg";

const ROUTE = [
  { icon: "ship", label: "Bay Shore" },
  { dot: "1", cls: "red", label: "Kismet", line: "" },
  { dot: "2", cls: "red", label: "Lighthouse", line: "" },
  { dot: "3", cls: "gold", label: "Sunken Forest", line: "gold" },
  { dot: "4", cls: "red", label: "Ocean Beach", line: "gold" },
  { icon: "home", label: "Bay Shore", line: "" },
];

const ITINERARY = [
  {
    t: "Morning",
    h: "Ferry to Kismet + Kismet Coffee + beach",
    d: "Catch the morning ferry from Bay Shore to Kismet. Grab a coffee, find your spot on the ocean beach, and get in the water. Lifeguards on duty in season.",
  },
  {
    t: "Mid-morning",
    h: "Walk west to the Fire Island Lighthouse",
    d: "Follow the shoreline west from Kismet — about a 20-minute walk along the ocean. Climb the 182 steps. On a clear day you can see across the Great South Bay.",
  },
  {
    t: "Midday",
    h: "Water taxi east to the Sunken Forest",
    d: "Walk back to Kismet and catch a water taxi east to Sailors Haven — home of the Sunken Forest, a 300-year-old maritime holly forest tucked behind the dunes. Ranger-guided tours run in season. Grab lunch at the Sassafras Café on the bay side.",
  },
  {
    t: "Afternoon",
    h: "Water taxi west to Ocean Beach",
    d: "Rinse off at the Sailors Haven showers, then catch a water taxi west to Ocean Beach. Browse Bay Walk, grab dinner at Matthew's Seafood, and stop at Unfriendly's for ice cream on the way to the ferry.",
  },
  {
    t: "Evening",
    h: "Evening ferry home from Ocean Beach",
    d: "Catch the evening ferry from Ocean Beach back to Bay Shore. Sit on the upper deck. Watch the sun drop behind the Lighthouse on the horizon.",
  },
];

const SurpriseResult = ({ onRetake }) => (
  <div>
    <div className="result-hero">
      <ImagePlaceholder
        label="The full Fire Island day — lighthouse, forest & a town worth staying for"
        rounded={false}
        src={surpriseHero}
        objectPosition="center 20%"
      />
      <div className="hero-overlay" />
      <div className="hero-text">
        <div className="hero-eyebrow">You asked for a surprise</div>
        <div className="hero-title">The Full Fire Island Day</div>
        <div className="hero-tagline">
          Lighthouse. Ancient forest. A town worth staying for. One day.
        </div>
      </div>
    </div>

    <div className="info-banner">
      <i className="far fa-compass info-icon" />
      <div className="info-text">
        <strong>
          For the curious. The explorers. The ones who want something different.
        </strong>
        Most people pick a town and stay there. This itinerary uses every ferry
        and water taxi on the island to show you three completely different
        sides of Fire Island in a single day.
      </div>
    </div>

    <div className="section-title">The route</div>
    <div className="route-bar">
      {ROUTE.map((stop, i) => (
        <React.Fragment key={stop.label + i}>
          {i > 0 && (
            <div className={`rline${stop.line === "gold" ? " gold" : ""}`} />
          )}
          <div className="route-stop">
            <div className={`rdot${stop.cls ? ` ${stop.cls}` : ""}`}>
              {stop.icon ? <i className={`far fa-${stop.icon}`} /> : stop.dot}
            </div>
            <div className="rlabel">{stop.label}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
    <div className="route-legend">
      <div className="rleg">
        <div className="rleg-line" style={{ background: "#cddbe8" }} />
        Ferry
      </div>
      <div className="rleg">
        <div className="rleg-line" style={{ background: "#4797db" }} />
        Water taxi
      </div>
    </div>

    <div className="insider-tips">
      <div className="insider-title">
        <i className="far fa-lightbulb" /> Before you go
      </div>
      <div className="qtip">
        <span className="qtip-mark">
          <i className="far fa-check" />
        </span>
        <span className="qtip-text">
          Start early — this is a full day. The first ferry to Kismet sets the
          whole day in motion.
        </span>
      </div>
      <div className="qtip">
        <span className="qtip-mark">
          <i className="far fa-check" />
        </span>
        <span className="qtip-text">
          The water taxi connects all communities — flag one down at any dock.
          Runs Memorial Day through Labor Day.
        </span>
      </div>
      <div className="qtip">
        <span className="qtip-mark">
          <i className="far fa-check" />
        </span>
        <span className="qtip-text">
          Wear comfortable shoes. You'll be on the beach, on boardwalks, and in
          a forest — all in one day.
        </span>
      </div>
    </div>

    <div className="captains-tip">
      <i className="far fa-anchor captain-icon" />
      <div>
        <div className="captain-label">Captain's Tip</div>
        <div className="captain-text">
          On the morning ferry to Kismet, grab the upper deck — you'll spot the
          Lighthouse before you even arrive. The water taxi is the thread that
          makes the whole day possible.
        </div>
      </div>
    </div>

    <div className="section-title">Your day, step by step</div>
    <div className="itinerary">
      {ITINERARY.map((s) => (
        <div className="itin-item" key={s.t}>
          <div className="itin-time">{s.t}</div>
          <div className="itin-content">
            <div className="itin-title">{s.h}</div>
            <div className="itin-detail">{s.d}</div>
          </div>
        </div>
      ))}
    </div>

    <div className="nature-box">
      <div className="nature-title">
        What makes the Sunken Forest worth the detour
      </div>
      <div className="nature-fact">
        <div className="ndot" />
        Ranked "globally rare" by the New York Natural Heritage Program — one of
        very few maritime holly forests remaining on Earth.
      </div>
      <div className="nature-fact">
        <div className="ndot" />
        Some American holly trees here are estimated to be over 300 years old —
        older than the United States.
      </div>
      <div className="nature-fact">
        <div className="ndot" />
        The forest appears "sunken" because a second sand dune traps it at sea
        level — salt spray prunes the canopy to an almost perfectly flat
        ceiling.
      </div>
    </div>

    <div className="divider" />
    <div className="section-title">Getting there</div>
    <div className="logistics-box">
      <div className="logistics-title">
        Bay Shore → Kismet → Water taxi throughout → Ocean Beach → Bay Shore
      </div>
      <div className="logistics-option">
        <div className="lbadge">Start</div>
        <div
          className="ldetail"
          dangerouslySetInnerHTML={{
            __html:
              "Ferry from <strong>Bay Shore terminal</strong> to Kismet. Park at the terminal or take the LIRR to <strong>Bay Shore, NY</strong> and walk or take <strong>David's Buses</strong>.",
          }}
        />
      </div>
      <div className="logistics-option">
        <div className="lbadge alt">Water taxi</div>
        <div
          className="ldetail"
          dangerouslySetInnerHTML={{
            __html:
              "The <strong>Fire Island Water Taxi</strong> connects all communities. Runs Memorial Day through Labor Day.",
          }}
        />
      </div>
      <div className="logistics-option">
        <div className="lbadge alt">End</div>
        <div
          className="ldetail"
          dangerouslySetInnerHTML={{
            __html:
              "Evening ferry from <strong>Ocean Beach</strong> back to Bay Shore. Check schedules at FireIslandFerries.com.",
          }}
        />
      </div>
    </div>

    <div className="read-more">
      <div className="read-more-title">What others are saying</div>
      <div className="rm-links">
        <a
          className="rm-link"
          href="https://www.nps.gov/places/the-sunken-forest-at-sailors-haven.htm"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div>
            <div className="rm-source">National Park Service</div>
            <div className="rm-title">The Sunken Forest at Sailors Haven</div>
            <div className="rm-desc">
              The official NPS guide — trail info, ecology, and visiting
              details.
            </div>
          </div>
          <div className="rm-arrow">→</div>
        </a>
        <a
          className="rm-link"
          href="https://obligatorytraveler.com/2025/05/14/fire-island-national-seashore-sailors-haven-day-trip-new-york/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div>
            <div className="rm-source">Obligatory Traveler</div>
            <div className="rm-title">
              Fire Island National Seashore — Sailors Haven Day Trip
            </div>
            <div className="rm-desc">
              A firsthand guide to Sailors Haven — what to expect and how to
              make the most of the forest.
            </div>
          </div>
          <div className="rm-arrow">→</div>
        </a>
        <a
          className="rm-link"
          href="https://www.svadore.com/sailors-haven-sunken-forest/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div>
            <div className="rm-source">Svadore</div>
            <div className="rm-title">
              Sailors Haven and Sunken Forest: How to Get There & What to Do
            </div>
            <div className="rm-desc">
              A detailed visitor's guide with photos, trail tips, and honest
              advice for first-timers.
            </div>
          </div>
          <div className="rm-arrow">→</div>
        </a>
      </div>
    </div>

    <div className="cta-row">
      <a className="cta-primary" href="#plan">
        Plan your trip →
      </a>
      <button type="button" className="cta-secondary" onClick={onRetake}>
        Not your vibe? Retake the quiz
      </button>
    </div>
  </div>
);

export default SurpriseResult;
